import * as Sentry from "@sentry/react";
import { useEffect, useRef, useState } from "react";

interface Window {
  webkitAudioContext?: typeof AudioContext;
}

interface UseAudioVisualizerOptions {
  bars: number;
  minBrightness: number;
  maxBrightness: number;
  trackOpacity: number;
}

interface UseAudioVisualizerReturn {
  audioRef: React.RefObject<HTMLAudioElement>;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  audioData: number[];
  togglePlay: () => void;
  seekAudio: (e: React.MouseEvent<HTMLDivElement>) => void;
  currentPosition: number;
  formatTime: (seconds: number) => string;
  options: UseAudioVisualizerOptions;
}

export const useAudioVisualizer = (
  audioSrc: string,
  options: Partial<UseAudioVisualizerOptions> = {},
): UseAudioVisualizerReturn => {
  const {
    bars = 50,
    minBrightness = 90,
    maxBrightness = 130,
    trackOpacity = 0.6,
  } = options;

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [audioData, setAudioData] = useState<number[]>([]);
  const [isAudioReady, setIsAudioReady] = useState(false);

  const audioRef = useRef<HTMLAudioElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyzerRef = useRef<AnalyserNode | null>(null);
  const sourceNodeRef = useRef<MediaElementAudioSourceNode | null>(null);
  const animationRef = useRef<number | null>(null);
  const audioBufferRef = useRef<AudioBuffer | null>(null);

  useEffect(() => {
    if (!audioSrc) return;

    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext ||
        (window as Window).webkitAudioContext)();
    }

    if (!analyzerRef.current) {
      const audioContext = audioContextRef.current;
      analyzerRef.current = audioContext.createAnalyser();
      analyzerRef.current.fftSize = 256;
      analyzerRef.current.smoothingTimeConstant = 0.8;
    }

    const fetchAudio = async () => {
      try {
        const response = await fetch(audioSrc);
        const arrayBuffer = await response.arrayBuffer();

        if (audioContextRef.current) {
          audioContextRef.current.decodeAudioData(arrayBuffer, (buffer) => {
            audioBufferRef.current = buffer;

            setDuration(buffer.duration);
            setIsAudioReady(true);

            const channelData = buffer.getChannelData(0);
            const blockSize = Math.floor(channelData.length / bars);
            const tempData = [];

            for (let i = 0; i < bars; i++) {
              let sum = 0;
              for (let j = 0; j < blockSize; j++) {
                const idx = i * blockSize + j;
                if (idx < channelData.length) {
                  sum += Math.abs(channelData[idx]);
                }
              }
              const average = (sum / blockSize) * 100;
              const scaled = Math.min(100, average * 15);
              tempData.push(scaled);
            }

            setAudioData(tempData);
          });
        }
      } catch (error) {
        Sentry.captureException(error, {
          level: "error",
          extra: { label: "Error decoding audio data" },
        });
      }
    };

    fetchAudio();

    return () => {
      if (sourceNodeRef.current) {
        sourceNodeRef.current.disconnect();
      }
    };
  }, [audioSrc, bars]);

  useEffect(() => {
    if (!audioRef.current || !audioContextRef.current || !analyzerRef.current)
      return;

    const setupAudioNodes = () => {
      if (!audioRef.current || !audioContextRef.current || !analyzerRef.current)
        return;

      if (!sourceNodeRef.current) {
        sourceNodeRef.current =
          audioContextRef.current.createMediaElementSource(audioRef.current);
        sourceNodeRef.current.connect(analyzerRef.current);
        analyzerRef.current.connect(audioContextRef.current.destination);
      }
    };

    const handleTimeUpdate = () => {
      if (audioRef.current) {
        setCurrentTime(audioRef.current.currentTime);
      }
    };

    const handlePlay = () => {
      if (audioContextRef.current?.state === "suspended") {
        audioContextRef.current.resume();
      }
      setupAudioNodes();
      setIsPlaying(true);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
      }
    };

    const audio = audioRef.current;
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("ended", handleEnded);

    if (audio.readyState >= 2 && !duration) {
      setDuration(audio.duration);
    }

    if (isAudioReady && audioRef.current && audioSrc) {
      audioRef.current.src = audioSrc;
    }

    const currentAnimationRef = animationRef.current;
    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("ended", handleEnded);

      if (currentAnimationRef) {
        cancelAnimationFrame(currentAnimationRef);
      }
    };
  }, [isAudioReady, audioSrc, duration]);

  const currentPosition = duration
    ? Math.floor((currentTime / duration) * bars)
    : 0;

  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      if (audioContextRef.current?.state === "suspended") {
        audioContextRef.current.resume();
      }

      const playPromise = audioRef.current.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => setIsPlaying(true))
          .catch(() => setIsPlaying(false));
      }
    }
  };

  const seekAudio = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !duration) return;

    const container = e.currentTarget;
    const rect = container.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;

    audioRef.current.currentTime = percentage * duration;
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  return {
    audioRef,
    isPlaying,
    currentTime,
    duration,
    audioData,
    togglePlay,
    seekAudio,
    currentPosition,
    formatTime,
    options: {
      bars,
      minBrightness,
      maxBrightness,
      trackOpacity,
    },
  };
};
